import { Fragment, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { enqueueSnackbar } from 'notistack';
import { Badge, Button } from '@monksflow/monks-ui';
import {
  closestCenter,
  DndContext,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from '@dnd-kit/core';
import { restrictToHorizontalAxis } from '@dnd-kit/modifiers';
import {
  horizontalListSortingStrategy,
  SortableContext,
  sortableKeyboardCoordinates,
} from '@dnd-kit/sortable';
import { ArrowUpRight, Plus } from 'lucide-react';
import {
  fieldPatternViolation,
  clampFieldValue,
  MAX_CELL_CODE_LENGTH,
  MAX_CELL_VALUE_LENGTH,
  type TaxonomyField,
  type TaxonomyFieldGroup,
  type TaxonomyFieldValue,
} from '@/entities/taxonomy';
import type { ResolvedList } from '@/entities/taxonomy-list';
import { cellKey, type SchemaProblem } from '../../lib/validateSchema';
import { resolveVariableForRow } from '../../lib/trackingPattern';
import { GridCell } from './GridCell';
import { SortableFieldHeaderCell } from './SortableFieldHeaderCell';
import { SubHeaderCell } from './SubHeaderCell';
import {
  DEFAULT_CODE_WIDTH,
  DEFAULT_VALUE_WIDTH,
  FocusTarget,
  GROUP_META,
  MIN_GROUP_WIDTH,
  RowWindow,
  Selection,
  Sub,
  describeProblems,
  isGeneratedType,
  mergeViolation,
  violationSeverityOf,
} from './fieldsEditorShared';
import { useReportedEditorOpen } from './useReportedEditorOpen';

interface GroupTableProps {
  group: TaxonomyFieldGroup;
  fields: TaxonomyField[];
  /** Every field in the schema — used to resolve a Variable's pattern names. */
  allFields: TaxonomyField[];
  /** Schema id → name, for linked-field subtitles/cells. */
  linkedSchemaNames: Record<string, string>;
  rowCount: number;
  /** Windowed row slice shared across both group tables; null = render all rows. */
  rowWindow: RowWindow | null;
  /** Reveals a row the window never rendered. Only the parent owns the scroller. */
  onScrollRowIntoView: (row: number) => void;
  readOnly: boolean;
  hoveredRow: number | null;
  onHoverRow: (row: number | null) => void;
  onSetCell: (fieldId: string, row: number, patch: Partial<TaxonomyFieldValue>) => void;
  onSetCells: (
    writes: { fieldId: string; row: number; sub: 'value' | 'code'; value: string }[],
  ) => void;
  onAddField: () => void;
  onEditField: (field: TaxonomyField) => void;
  /** Open the Variable (computed) pattern composer. */
  onConfigureField: (field: TaxonomyField) => void;
  /** Reset a library field's values to the catalog defaults. */
  onResetField: (field: TaxonomyField) => void;
  /** Bind this column to a shared list / release it back to inline values. */
  onLinkList: (field: TaxonomyField) => void;
  onUnlinkList: (field: TaxonomyField) => void;
  /** Vocabulary resolved from the bound list, keyed by field id. */
  resolvedLists: Record<string, ResolvedList>;
  /** Validate findings per cell / per column — see {@link FieldsEditorProps}. */
  cellProblems?: Map<string, SchemaProblem[]>;
  fieldProblems?: Map<string, SchemaProblem[]>;
  /** A cell or column to reveal, already filtered to this group. */
  focusTarget?: FocusTarget | null;
  /** Preview a sample code for a Variable column; nothing is minted. */
  onGenerateSample: (field: TaxonomyField) => void;
  onAddFieldAfter: (field: TaxonomyField) => void;
  onAddSubfield: (field: TaxonomyField) => void;
  onDetachField: (id: string) => void;
  onDuplicateField: (field: TaxonomyField) => void;
  onRemoveField: (id: string) => void;
  onResize: (fieldId: string, patch: Partial<TaxonomyField>) => void;
  onReorder: (activeId: string, overId: string) => void;
  onRename: (id: string, name: string) => void;
  /** `key` separates the cell editor from a column rename in the same table. */
  onEditingChange: (key: 'cell' | 'header', open: boolean) => void;
}

/** One bordered, accent-topped table for a single field group. */
export function GroupTable(props: GroupTableProps) {
  const { t } = useTranslation('knowledge');
  const {
    group,
    fields,
    allFields,
    linkedSchemaNames,
    rowCount,
    rowWindow,
    onScrollRowIntoView,
    readOnly,
    hoveredRow,
    onHoverRow,
    focusTarget,
  } = props;
  const meta = GROUP_META[group];
  const rows = Array.from({ length: rowCount }, (_, i) => i);
  const [resizing, setResizing] = useState<{
    id: string;
    col: 'value' | 'code';
    width: number;
  } | null>(null);

  const colWidth = (field: TaxonomyField, col: 'value' | 'code') => {
    if (resizing && resizing.id === field.id && resizing.col === col) return resizing.width;
    const stored = col === 'value' ? field.valueWidth : field.codeWidth;
    return stored ?? (col === 'value' ? DEFAULT_VALUE_WIDTH : DEFAULT_CODE_WIDTH);
  };

  // Explicit table width = sum of the (resizable) column widths. table-fixed
  // makes those widths authoritative; an explicit px width (not 100%) keeps the
  // box hugging the table instead of grabbing the whole flex row.
  //
  // Floored at MIN_GROUP_WIDTH so the group header always has a single line to
  // render on. Any slack goes to the columns, which is the point — a lone field
  // in a 200px table is what squeezed the header in the first place.
  const tableWidth = Math.max(
    MIN_GROUP_WIDTH,
    fields.reduce((sum, f) => sum + colWidth(f, 'value') + colWidth(f, 'code'), 0),
  );

  const totalCols = fields.length * 2;

  const fieldIds = fields.map((f) => f.id);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
  );
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    props.onReorder(String(active.id), String(over.id));
  };

  // ─── Spreadsheet selection + copy/paste (Google-Sheets-style) ─────────────
  // Cells are addressed by a flat sub-column index `c` (0..colCount-1): even = a
  // field's VALUE, odd = its CODE. A selection is an anchor→focus rectangle.
  const colCount = fields.length * 2;
  const gridRef = useRef<HTMLDivElement>(null);
  const [sel, setSel] = useState<Selection | null>(null);
  const [edit, setEditState] = useState<{ r: number; c: number } | null>(null);
  const reportCellEditor = useReportedEditorOpen((open) => props.onEditingChange('cell', open));
  const setEdit = (next: { r: number; c: number } | null) => {
    setEditState(next);
    reportCellEditor(next !== null);
  };
  const [editDraft, setEditDraft] = useState('');
  const draggingSel = useRef(false);

  useEffect(() => {
    const up = () => {
      draggingSel.current = false;
    };
    window.addEventListener('mouseup', up);
    return () => window.removeEventListener('mouseup', up);
  }, []);

  const rect = sel
    ? {
        r0: Math.min(sel.ar, sel.fr),
        r1: Math.max(sel.ar, sel.fr),
        c0: Math.min(sel.ac, sel.fc),
        c1: Math.max(sel.ac, sel.fc),
      }
    : null;
  const inRect = (r: number, c: number) =>
    !!rect && r >= rect.r0 && r <= rect.r1 && c >= rect.c0 && c <= rect.c1;
  const fieldOfCol = (c: number) => fields[Math.floor(c / 2)];
  const subOfCol = (c: number): Sub => (c % 2 === 0 ? 'value' : 'code');
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cell lookups index fields[] / resolvedLists by a coordinate that can fall outside
  const isDateCol = (c: number) => subOfCol(c) === 'value' && fieldOfCol(c)?.type === 'date';
  /** A column bound to a shared list is owned by that list — not editable here. */
  const isListBound = (f: TaxonomyField | undefined) => !!f?.listRef?.listId;
  // Variable (computed), Linked and list-bound columns are all derived — never writable.
  const isDerivedCol = (c: number) => {
    const f = fieldOfCol(c);
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cell lookups index fields[] / resolvedLists by a coordinate that can fall outside
    return !!f && (isGeneratedType(f.type) || isListBound(f));
  };
  /** Cell text: list-bound columns render the resolved list vocabulary. */
  const valAt = (r: number, c: number) => {
    const f = fieldOfCol(c);
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cell lookups index fields[] / resolvedLists by a coordinate that can fall outside
    if (!f) return '';
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cell lookups index fields[] / resolvedLists by a coordinate that can fall outside
    if (isListBound(f)) return props.resolvedLists[f.id]?.values[r]?.[subOfCol(c)] ?? '';
    // A Variable draws ONE merged cell across both sub-columns, so its preview
    // belongs on the value half and the code half stays empty. Reading
    // `f.values` here returned two empties, so pasting a copied row into a
    // sheet silently dropped the generated code. Emitting the merged cell ONCE
    // is the copy loop's job, not this one's.
    if (f.type === 'computed')
      return subOfCol(c) === 'value' ? resolveVariableForRow(f, allFields, r) : '';
    // A linked column has no per-row value at all: what renders there is the
    // target schema's NAME, and the join itself resolves at asset-tagging time.
    if (f.type === 'linked') return '';
    return f.values[r]?.[subOfCol(c)] ?? '';
  };
  /**
   * Both cell-write funnels clamp, because only one of them passes through the
   * editor: a block paste is handled on the grid and never reaches an `<input>`,
   * so `maxLength` alone would leave the likelier route — a column pasted out of
   * a spreadsheet — writing values the API rejects.
   */
  const setAt = (r: number, c: number, v: string) => {
    const f = fieldOfCol(c);
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cell lookups index fields[] / resolvedLists by a coordinate that can fall outside
    if (f && !isGeneratedType(f.type) && !isListBound(f)) {
      const sub = subOfCol(c);
      props.onSetCell(f.id, r, { [sub]: clampFieldValue(sub, v) });
    }
  };
  /**
   * Batched write of many cells at once (paste/fill/clear) — see onSetCells.
   * Returns the cells it skipped because their column gets its values elsewhere.
   */
  const setMany = (cells: { r: number; c: number; v: string }[]) => {
    const writable = (c: number) => {
      const f = fieldOfCol(c);
      // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cell lookups index fields[] / resolvedLists by a coordinate that can fall outside
      return f && !isGeneratedType(f.type) && !isListBound(f);
    };
    const writes = cells
      .filter(({ c }) => writable(c))
      .map(({ r, c, v }) => {
        const sub = subOfCol(c);
        return { fieldId: fieldOfCol(c).id, row: r, sub, value: clampFieldValue(sub, v) };
      });
    props.onSetCells(writes);
    return { written: writes.length, skipped: cells.filter(({ c }) => !writable(c)) };
  };

  const beginEdit = (r: number, c: number, initial?: string) => {
    // Date + generated (Variable / Linked) cells aren't edited via the text editor.
    if (readOnly || isDateCol(c) || isDerivedCol(c)) return;
    setEdit({ r, c });
    setEditDraft(initial ?? valAt(r, c));
  };
  /**
   * Escape drops the edit, but focusing the grid blurs the `<input>` first and
   * GridCell commits on blur — reaching `commitEdit` with an `edit`/`editDraft`
   * React has not re-rendered yet, so the discarded draft got written anyway.
   * A ref is read synchronously, which is the whole reason it is not state.
   */
  const discardingRef = useRef(false);
  const commitEdit = () => {
    if (discardingRef.current) {
      setEdit(null);
      return;
    }
    if (edit && !isDateCol(edit.c)) setAt(edit.r, edit.c, editDraft);
    setEdit(null);
  };

  /**
   * Commits when the virtualizer drops the row being edited.
   *
   * The `<input>` unmounts with the row while `edit` survives in state, so the
   * draft used to sit there until the next mousedown wrote it — into a row the
   * user had scrolled away from and could not see. Committing keeps what they
   * typed where they typed it; clearing would silently delete it.
   *
   * Still needed now that arrow keys scroll: a wheel scroll can drop the row
   * without touching the selection, and fast key-repeat can outrun the render
   * that updates `rowWindow`.
   */
  useEffect(() => {
    if (edit && rowWindow && !rowWindow.indexes.includes(edit.r)) commitEdit();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- commitEdit closes over `editDraft`; re-running on every keystroke would commit mid-typing
  }, [rowWindow, edit]);

  const onCellMouseDown = (r: number, c: number, e: React.MouseEvent) => {
    pendingRevealRef.current = null;
    if (edit) commitEdit();
    e.preventDefault();
    // The grid is as tall as all its rows, so a plain focus scrolls it into view
    // mid-press, and the drag then selects every cell that slid under the pointer.
    gridRef.current?.focus({ preventScroll: true });
    if (e.shiftKey && sel) setSel({ ...sel, fr: r, fc: c });
    else setSel({ ar: r, ac: c, fr: r, fc: c });
    draggingSel.current = true;
  };
  const onCellMouseEnter = (r: number, c: number) => {
    if (draggingSel.current) setSel((s) => (s ? { ...s, fr: r, fc: c } : s));
  };

  const clamp = (n: number, min: number, max: number) => Math.max(min, Math.min(max, n));

  /**
   * A cell `revealCell` could only reach through the virtualizer, revealed again
   * once its row mounts. The virtualizer scrolls only the board; the page around
   * it may still hide the row, and the cell's own `scrollIntoView` moves both.
   */
  const pendingRevealRef = useRef<{ r: number; c: number } | null>(null);
  useEffect(() => {
    const pending = pendingRevealRef.current;
    if (!pending) return;
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- as in revealCell
    const cell = gridRef.current?.querySelector<HTMLElement>(
      `[data-cell="${pending.r}-${pending.c}"]`,
    );
    if (!cell) return;
    pendingRevealRef.current = null;
    cell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  }, [rowWindow]);

  /**
   * Brings the cell the selection just moved to onto the screen.
   *
   * Prefers the DOM, which covers both axes at once and is the ONLY thing that
   * works below the virtualization threshold — the virtualizer is disabled
   * there, and a disabled virtualizer has no measurements, so `scrollToIndex`
   * silently does nothing. Falls back to the parent's virtualizer only when the
   * row was never rendered, which is the one case the DOM cannot reach.
   *
   * Without this, arrowing down a windowed grid walked the selection into rows
   * that were never mounted: no focus ring moved, the scroller stayed at 0, and
   * a subsequent keystroke edited a row the user could not see.
   */
  const revealCell = (r: number, c: number) => {
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- the rule cannot pin the non-deprecated `querySelector` overload for a generic element
    const cell = gridRef.current?.querySelector<HTMLElement>(`[data-cell="${r}-${c}"]`);
    if (cell) {
      pendingRevealRef.current = null;
      cell.scrollIntoView({ block: 'nearest', inline: 'nearest' });
      return;
    }
    // Unwindowed, every row is rendered, so a miss has nothing to wait for.
    pendingRevealRef.current = rowWindow ? { r, c } : null;
    onScrollRowIntoView(r);
    /*
     * A group whose fields are ALL empty renders no row at all, and the
     * virtualizer is disabled below the threshold — so neither branch above can
     * move anything. That is the state a group is in while its fields are still
     * being drafted, which is exactly when `field_has_no_values` fires, so a
     * jump to that finding otherwise did nothing at all.
     *
     * The header is the only node that marks a column, and a column finding is
     * explained there anyway. It is sticky, so `block: 'nearest'` cannot move the
     * grid vertically; the horizontal scroll is the part that matters.
     */
    // eslint-disable-next-line @typescript-eslint/no-deprecated -- as above
    const header = gridRef.current?.querySelector<HTMLElement>(`[data-col="${c}"]`);
    header?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  };

  /**
   * Reveals the cell or column the details popover asked for.
   *
   * The same three steps a click already performs — reveal, select, return focus
   * to the grid — reused rather than reinvented, so a jumped-to cell behaves
   * exactly like a clicked one and the arrow keys carry on from there.
   *
   * A column finding has no row, so it lands on row 0 of that column: the header
   * carries the explanation, and scrolling the column into view is the part the
   * user cannot do for themselves.
   */
  useEffect(() => {
    if (!focusTarget) return;
    const index = fields.findIndex((f) => f.id === focusTarget.fieldId);
    if (index < 0) return;

    const column = index * 2 + (focusTarget.column === 'code' ? 1 : 0);
    const row = focusTarget.row ?? 0;

    // Past the window the row is not mounted, so `revealCell` falls back to the
    // virtualizer — which is the whole reason this exists.
    revealCell(row, column);
    setSel({ ar: row, ac: column, fr: row, fc: column });
    gridRef.current?.focus({ preventScroll: true });
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `nonce` is what makes a repeat click repeat; the rest are stable for a given target
  }, [focusTarget?.nonce]);

  const onGridKeyDown = (e: React.KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (edit || !sel || !rect || readOnly) return;
    if (e.ctrlKey || e.metaKey) return; // copy/cut/paste handled by native events
    if (e.key === 'Delete' || e.key === 'Backspace') {
      e.preventDefault();
      clearRect();
      return;
    }
    if (e.key === 'Enter' || e.key === 'F2') {
      e.preventDefault();
      beginEdit(sel.fr, sel.fc);
      return;
    }
    const moves: Record<string, [number, number]> = {
      ArrowUp: [-1, 0],
      ArrowDown: [1, 0],
      ArrowLeft: [0, -1],
      ArrowRight: [0, 1],
    };
    const m = moves[e.key];
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cell lookups index fields[] / resolvedLists by a coordinate that can fall outside
    if (m) {
      e.preventDefault();
      const nr = clamp(sel.fr + m[0], 0, Math.max(0, rowCount - 1));
      const nc = clamp(sel.fc + m[1], 0, colCount - 1);
      if (e.shiftKey) setSel({ ...sel, fr: nr, fc: nc });
      else setSel({ ar: nr, ac: nc, fr: nr, fc: nc });
      revealCell(nr, nc);
      return;
    }
    if (e.key.length === 1 && !e.altKey) {
      // The editor mounts and takes focus before the browser inserts the key, so an unprevented
      // keydown types it a second time into a draft already seeded with it.
      e.preventDefault();
      beginEdit(sel.fr, sel.fc, e.key);
    }
  };

  const onGridCopy = (e: React.ClipboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (edit || !rect) return;
    const lines: string[] = [];
    for (let r = rect.r0; r <= rect.r1; r++) {
      const cols: string[] = [];
      for (let c = rect.c0; c <= rect.c1; c++) {
        const f = fieldOfCol(c);
        // A generated column draws ONE cell across both sub-columns, so it
        // contributes one value — otherwise a copied row carried a phantom
        // empty per merged column and pasting it landed a column out of step.
        // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition -- cell lookups index fields[] by a coordinate that can fall outside
        if (f && isGeneratedType(f.type)) {
          const vc = Math.floor(c / 2) * 2;
          // Emit at the value half, or at the code half when that is all the
          // rect covers — either way exactly once per merged cell.
          if (c !== Math.max(vc, rect.c0)) continue;
          cols.push(valAt(r, vc));
          continue;
        }
        cols.push(valAt(r, c));
      }
      lines.push(cols.join('\t'));
    }
    e.clipboardData.setData('text/plain', lines.join('\n'));
    e.preventDefault();
  };

  const clearRect = () => {
    if (!rect) return;
    const cells: { r: number; c: number; v: string }[] = [];
    for (let r = rect.r0; r <= rect.r1; r++)
      for (let c = rect.c0; c <= rect.c1; c++) cells.push({ r, c, v: '' });
    const { written, skipped } = setMany(cells);
    // Only when nothing was cleared: then the key appears to do nothing at all.
    if (written === 0 && skipped.length > 0) {
      enqueueSnackbar(t('taxonomy.fieldsEditor.clearSkipped'), {
        variant: 'info',
        preventDuplicate: true,
      });
    }
  };

  const onGridCut = (e: React.ClipboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (edit || !rect) return;
    onGridCopy(e);
    if (!readOnly) clearRect();
  };

  const onGridPaste = (e: React.ClipboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;
    if (edit || !rect || readOnly) return;
    const text = e.clipboardData.getData('text/plain');
    if (!text) return;
    e.preventDefault();
    const grid = text
      .replace(/\r\n?/g, '\n')
      .split('\n')
      .map((l) => l.split('\t'));
    if (grid.length > 1 && grid[grid.length - 1].length === 1 && grid[grid.length - 1][0] === '')
      grid.pop();
    if (grid.length === 0) return;
    const cells: { r: number; c: number; v: string }[] = [];
    // A single copied value fills the whole current selection (Sheets behavior).
    if (grid.length === 1 && grid[0].length === 1 && (rect.r1 > rect.r0 || rect.c1 > rect.c0)) {
      const v = grid[0][0];
      for (let r = rect.r0; r <= rect.r1; r++)
        for (let c = rect.c0; c <= rect.c1; c++) cells.push({ r, c, v });
    } else {
      // Otherwise spill the block from the selection's top-left.
      grid.forEach((line, dr) =>
        line.forEach((v, dc) => {
          const c = rect.c0 + dc;
          if (c < colCount) cells.push({ r: rect.r0 + dr, c, v });
        }),
      );
    }
    const dropped = setMany(cells).skipped.filter(({ v }) => v !== '').length;
    if (dropped > 0) {
      enqueueSnackbar(t('taxonomy.fieldsEditor.pasteSkipped', { count: dropped }), {
        variant: 'warning',
      });
    }
  };

  const onEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      commitEdit();
      gridRef.current?.focus({ preventScroll: true });
      if (edit) {
        // Sheets behaviour: committing moves down, so a column can be typed
        // without reaching for the mouse. Clamped exactly like the arrow keys.
        const nr = clamp(edit.r + 1, 0, Math.max(0, rowCount - 1));
        setSel({ ar: nr, ac: edit.c, fr: nr, fc: edit.c });
        revealCell(nr, edit.c);
      }
    } else if (e.key === 'Escape') {
      e.preventDefault();
      discardingRef.current = true;
      setEdit(null);
      // Fires the blur commit synchronously, which the flag above swallows.
      gridRef.current?.focus({ preventScroll: true });
      discardingRef.current = false;
    }
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-static-element-interactions -- scroll container that owns the spreadsheet key handling; the tables inside carry the semantics
    <div
      ref={gridRef}
      tabIndex={readOnly ? undefined : 0}
      onKeyDown={onGridKeyDown}
      onCopy={onGridCopy}
      onCut={onGridCut}
      onPaste={onGridPaste}
      // The cells draw the grid's lines, so the rounded clip cuts them at the bottom
      // corners; the overlay draws the outline, curve included, on top of them. It sits
      // under the sticky header, which keeps its own rounded top while scrolled.
      className="relative shrink-0 overflow-clip rounded-lg outline-none after:pointer-events-none after:absolute after:inset-0 after:z-20 after:rounded-lg after:border after:border-border-light"
    >
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        modifiers={[restrictToHorizontalAxis]}
        onDragEnd={handleDragEnd}
      >
        <table
          className="table-fixed border-separate border-spacing-0 text-sm"
          style={{ width: tableWidth }}
        >
          <colgroup>
            {fields.map((field) => (
              <Fragment key={field.id}>
                <col style={{ width: colWidth(field, 'value') }} />
                <col style={{ width: colWidth(field, 'code') }} />
              </Fragment>
            ))}
          </colgroup>
          <thead className="sticky top-0 z-30">
            {/* Group label + accent — part of the sticky header so it (and the
              colored bar) stay pinned with the column headers while scrolling. */}
            <tr>
              <th
                colSpan={totalCols}
                // The square backing fills the corners outside this cell's curve, so lines
                // scrolled under the pinned header do not show there.
                className="relative rounded-t-lg border-x border-t border-border-light bg-card px-3 py-2 text-left before:absolute before:-inset-px before:-z-10 before:bg-card"
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <span className={`size-2 shrink-0 rounded-full ${meta.dot}`} aria-hidden />
                    <h3 className="whitespace-nowrap text-sm font-semibold text-foreground">
                      {meta.label}
                    </h3>
                    <Badge variant="ghost" roundness="full" className="border-border-light text-xs">
                      {fields.length}
                    </Badge>
                  </div>
                  {!readOnly && (
                    <Button
                      variant="ghost"
                      size="mini"
                      className="h-7 shrink-0 whitespace-nowrap text-xs text-muted-foreground"
                      onClick={props.onAddField}
                    >
                      <Plus className="size-3.5" />
                      {t('taxonomy.fieldsEditor.addField')}
                    </Button>
                  )}
                </div>
              </th>
            </tr>
            <tr>
              <SortableContext items={fieldIds} strategy={horizontalListSortingStrategy}>
                {fields.map((field, i) => (
                  <SortableFieldHeaderCell
                    key={field.id}
                    field={field}
                    allFields={allFields}
                    targetName={
                      field.link?.targetSchemaId
                        ? linkedSchemaNames[field.link.targetSchemaId]
                        : undefined
                    }
                    isLast={i === fields.length - 1}
                    accent={meta.accent}
                    readOnly={readOnly}
                    onRename={(name) => props.onRename(field.id, name)}
                    onRenameEditingChange={(open) => props.onEditingChange('header', open)}
                    onEdit={() => props.onEditField(field)}
                    onConfigure={() => props.onConfigureField(field)}
                    onReset={() => props.onResetField(field)}
                    onLinkList={() => props.onLinkList(field)}
                    onUnlinkList={() => props.onUnlinkList(field)}
                    boundList={props.resolvedLists[field.id]}
                    problems={props.fieldProblems?.get(field.id)}
                    onGenerateSample={() => props.onGenerateSample(field)}
                    onAddRight={() => props.onAddFieldAfter(field)}
                    onAddSubfield={() => props.onAddSubfield(field)}
                    onDetach={() => props.onDetachField(field.id)}
                    onDuplicate={() => props.onDuplicateField(field)}
                    onRemove={() => props.onRemoveField(field.id)}
                  />
                ))}
              </SortableContext>
            </tr>
            <tr>
              {fields.map((field, i) => (
                <Fragment key={field.id}>
                  <SubHeaderCell
                    label={t('taxonomy.fieldsEditor.value')}
                    readOnly={readOnly}
                    col={i * 2}
                    width={colWidth(field, 'value')}
                    onLive={(w) => setResizing({ id: field.id, col: 'value', width: w })}
                    onCommit={(w) => {
                      setResizing(null);
                      props.onResize(field.id, { valueWidth: w });
                    }}
                  />
                  <SubHeaderCell
                    label={t('taxonomy.fieldsEditor.code')}
                    readOnly={readOnly}
                    col={i * 2 + 1}
                    width={colWidth(field, 'code')}
                    isLast={i === fields.length - 1}
                    onLive={(w) => setResizing({ id: field.id, col: 'code', width: w })}
                    onCommit={(w) => {
                      setResizing(null);
                      props.onResize(field.id, { codeWidth: w });
                    }}
                  />
                </Fragment>
              ))}
            </tr>
          </thead>
          <tbody>
            {/* Windowing: a tall spacer <tr> reserves the off-screen rows above
              the visible slice (mirrors the Trackers board). */}
            {rowWindow && rowWindow.paddingTop > 0 && (
              <tr aria-hidden>
                <td colSpan={totalCols} style={{ height: rowWindow.paddingTop, padding: 0 }} />
              </tr>
            )}
            {(rowWindow ? rowWindow.indexes : rows).map((row) => (
              <tr
                key={row}
                className={hoveredRow === row ? 'bg-muted/40' : undefined}
                onMouseEnter={() => onHoverRow(row)}
                onMouseLeave={() => onHoverRow(null)}
              >
                {fields.map((field, fi) => {
                  const vc = fi * 2;
                  const cc = fi * 2 + 1;
                  // Generated columns (Variable / Linked): auto-resolved, no editable
                  // cells — one muted, non-interactive cell spanning both sub-columns.
                  if (isGeneratedType(field.type)) {
                    const isLastField = fi === fields.length - 1;
                    // Variable resolves a per-row preview; a linked join key resolves at
                    // asset-tagging time, so show a placeholder in the builder.
                    const generated =
                      field.type === 'computed' ? resolveVariableForRow(field, allFields, row) : '';
                    const linked = field.type === 'linked';
                    const targetName = field.link?.targetSchemaId
                      ? linkedSchemaNames[field.link.targetSchemaId]
                      : undefined;
                    // One cell spans BOTH sub-columns, so it belongs to the
                    // selection whenever either half is in the rect. Without
                    // this the column stayed untinted and unclickable while its
                    // neighbours highlighted around it — and it is copyable, so
                    // looking unselectable was the misleading part.
                    const genSelected = inRect(row, vc) || inRect(row, cc);
                    const genFocus = !!sel && sel.fr === row && (sel.fc === vc || sel.fc === cc);
                    return (
                      <td
                        key={field.id}
                        colSpan={2}
                        data-cell={`${row}-${vc}`}
                        className={`select-none border-b border-l border-border-light ${
                          genSelected ? 'bg-primary/10' : 'bg-surface/30'
                        } ${genFocus ? 'z-10 ring-2 ring-inset ring-primary' : ''} ${
                          isLastField ? 'border-r' : ''
                        }`}
                        // Mirrors GridCell: `readOnly` strips the handlers, and
                        // nothing here is editable anyway — selection and copy
                        // are the whole interaction.
                        {...(readOnly
                          ? {}
                          : {
                              onMouseDown: (e: React.MouseEvent) => onCellMouseDown(row, vc, e),
                              onMouseEnter: () => onCellMouseEnter(row, cc),
                            })}
                      >
                        <div
                          className="flex h-9 items-center gap-1 truncate px-2 font-mono text-xs text-muted-foreground"
                          title={linked ? undefined : generated || undefined}
                        >
                          {linked ? (
                            field.link?.targetSchemaId ? (
                              <>
                                <ArrowUpRight className="size-3 shrink-0" aria-hidden />
                                <span className="truncate">
                                  {targetName ?? t('taxonomy.fieldsEditor.linked')}
                                </span>
                              </>
                            ) : (
                              <span className="text-muted-foreground/40">
                                {t('taxonomy.fieldsEditor.notConfigured')}
                              </span>
                            )
                          ) : (
                            generated || <span className="text-muted-foreground/40">—</span>
                          )}
                        </div>
                      </td>
                    );
                  }
                  // A list-bound column is owned by the shared list: still
                  // selectable/copyable, but not editable from the schema — which
                  // is enforced by `beginEdit`/`setAt`/`setMany`, NOT by
                  // `readOnly`, whose extra effect is to strip mouse handlers.
                  // Those three cover the edit/commit flow only, so the date
                  // picker — which writes directly — is gated by `isBound`.
                  // The live pattern check only looks at the VALUE column: the
                  // backend enforces the pattern against the value a caller
                  // supplies, so flagging the code column would warn about
                  // something generation never tests. Validate findings for
                  // either column are merged in on top.
                  const liveViolation = fieldPatternViolation(
                    valAt(row, vc),
                    field.validationPattern,
                  );
                  const valueProblems = props.cellProblems?.get(cellKey(field.id, row, 'value'));
                  const codeProblems = props.cellProblems?.get(cellKey(field.id, row, 'code'));
                  return (
                    <Fragment key={field.id}>
                      <GridCell
                        value={valAt(row, vc)}
                        isDate={field.type === 'date'}
                        mask={field.format}
                        violation={mergeViolation(liveViolation, valueProblems)}
                        violationSeverity={violationSeverityOf(liveViolation, valueProblems)}
                        selected={inRect(row, vc)}
                        isFocus={!!sel && sel.fr === row && sel.fc === vc}
                        editing={!!edit && edit.r === row && edit.c === vc}
                        dataCell={`${row}-${vc}`}
                        maxLength={MAX_CELL_VALUE_LENGTH}
                        // NOT `|| boundCol`: `readOnly` also strips every mouse
                        // handler, which took away the selecting and copying the
                        // comment above promises. Editing is already refused by
                        // `beginEdit` via `isDerivedCol`, and by `setAt`/`setMany`.
                        readOnly={readOnly}
                        // The one write path those three guards do NOT cover is
                        // the date picker, which writes through `onDateChange`
                        // rather than the edit/commit flow.
                        isBound={isListBound(field)}
                        lastCol={vc === colCount - 1}
                        editDraft={editDraft}
                        onMouseDown={(e) => onCellMouseDown(row, vc, e)}
                        onMouseEnter={() => onCellMouseEnter(row, vc)}
                        onDoubleClick={() => beginEdit(row, vc)}
                        onEditChange={setEditDraft}
                        onEditKeyDown={onEditKeyDown}
                        onCommit={commitEdit}
                        onDateChange={(v) => setAt(row, vc, v)}
                      />
                      <GridCell
                        value={valAt(row, cc)}
                        muted
                        violation={describeProblems(codeProblems)}
                        violationSeverity={violationSeverityOf(null, codeProblems)}
                        selected={inRect(row, cc)}
                        isFocus={!!sel && sel.fr === row && sel.fc === cc}
                        editing={!!edit && edit.r === row && edit.c === cc}
                        dataCell={`${row}-${cc}`}
                        maxLength={MAX_CELL_CODE_LENGTH}
                        // See the value cell above — editing is guarded elsewhere.
                        readOnly={readOnly}
                        // The code half is owned by the list too, and the user
                        // double-clicks it just as often as the value half.
                        isBound={isListBound(field)}
                        lastCol={cc === colCount - 1}
                        editDraft={editDraft}
                        onMouseDown={(e) => onCellMouseDown(row, cc, e)}
                        onMouseEnter={() => onCellMouseEnter(row, cc)}
                        onDoubleClick={() => beginEdit(row, cc)}
                        onEditChange={setEditDraft}
                        onEditKeyDown={onEditKeyDown}
                        onCommit={commitEdit}
                        onDateChange={(v) => setAt(row, cc, v)}
                      />
                    </Fragment>
                  );
                })}
                {/* No per-row ✕ any more: rows blank across every editable
                    column are pruned automatically on commit. The button it
                    replaced deleted from a bound column's hidden inline array
                    and looked like it always removed the last row. */}
              </tr>
            ))}
            {rowWindow && rowWindow.paddingBottom > 0 && (
              <tr aria-hidden>
                <td colSpan={totalCols} style={{ height: rowWindow.paddingBottom, padding: 0 }} />
              </tr>
            )}
          </tbody>
        </table>
      </DndContext>
    </div>
  );
}
